import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AuthForm } from './components/auth/AuthForm';
import { Layout } from './components/layout/Layout';
import { AdminPanel } from './components/admin/AdminPanel';
import { CreditReportPage } from './pages/CreditReportPage';
import { UserPage } from './pages/UserPage';
import { Loader2 } from 'lucide-react';
import { ConvexRealtimeProvider } from './contexts/ConvexRealtimeProvider';

function AppContent() {
  const { isAuthenticated, isLoading, manager } = useAuth();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);

  // Check manager session and update admin status whenever location changes
  useEffect(() => {
    const checkAdminStatus = () => {
      const managerSession = localStorage.getItem('managerSession');
      const hasManagerToken = !!localStorage.getItem('managerAccessToken');
      const hasManagerRefresh = !!localStorage.getItem('managerRefreshToken');
      if (managerSession && (hasManagerToken || hasManagerRefresh)) {
        try {
          const session = JSON.parse(managerSession);
          setIsAdmin(session.role === 'admin');
        } catch {
          setIsAdmin(false);
        }
      } else {
        if (managerSession && !hasManagerToken && !hasManagerRefresh) {
          localStorage.removeItem('managerSession');
        }
        setIsAdmin(false);
      }
    };

    checkAdminStatus();

    // Listen for storage changes (in case of updates from other components)
    window.addEventListener('storage', checkAdminStatus);
    
    // Custom event for immediate updates within the same window
    window.addEventListener('managerSessionChange', checkAdminStatus);

    return () => {
      window.removeEventListener('storage', checkAdminStatus);
      window.removeEventListener('managerSessionChange', checkAdminStatus);
    };
  }, [location]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="text-lg">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <AuthForm />}
      />
      <Route
        path="/admin"
        element={
          isAdmin ? (
            <AdminPanel />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/admin/dispute-review"
        element={
          isAdmin || manager ? (
            <Navigate to="/admin?tab=disputes" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/users/:userId"
        element={<UserPage />}
      />
      <Route
        path="/credit-report"
        element={
          isAuthenticated ? (
            <CreditReportPage />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/*"
        element={
          isAuthenticated ? (
            <ConvexRealtimeProvider>
              <Layout />
            </ConvexRealtimeProvider>
          ) : isAdmin ? (
            <Navigate to="/admin" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
